import type { PremiumPlanId } from '~/types/premium'

/**
 * Reports purchases and opened checkouts to Google Ads and GA4.
 *
 * Called where the page learns from the webhook that a payment landed (the
 * checkout modal, the guest and pack checkouts in useCvOffers), not on the
 * LemonSqueezy redirect: that redirect lands inside the checkout iframe.
 *
 * The Google tag (AW-1040350636, GA4 G-W016HNLYMB as its destination) is
 * configured by GTM; this only sends events through it. Both events name their
 * destination, so the Ads conversion is not counted twice.
 */
/** Ads conversion action "Purchase - InMyResume" (7355121791), account 491-053-2870. */
const ADS_PURCHASE = 'AW-1040350636/VcuZCP-AmbMbEKz7ifAD'
/**
 * Ads conversion action "Begin checkout - InMyResume" (7822272134). Secondary
 * in the account, so the insurance campaigns bidding on BEGIN_CHECKOUT do not
 * count it; the InMyResume campaign bids on it through its custom goal, which
 * gives the bidding something to learn from while purchases are few.
 */
const ADS_BEGIN_CHECKOUT = 'AW-1040350636/itGoCIbN-ZEdEKz7ifAD'
/** Kept low against a purchase's real price, for when bidding moves to value. */
const BEGIN_CHECKOUT_VALUE = 1
const GA4_ID = 'G-W016HNLYMB'
/** Transaction ids already reported from this browser, so a retry does not count twice. */
const REPORTED_KEY = 'adsReportedPurchases'

type Gtag = (...args: unknown[]) => void

export function useAdsConversion() {
  const { plans, getPlan } = usePremiumPlans()

  function alreadyReported(transactionId: string): boolean {
    try {
      const reported: string[] = JSON.parse(localStorage.getItem(REPORTED_KEY) || '[]')
      if (reported.includes(transactionId)) return true
      localStorage.setItem(REPORTED_KEY, JSON.stringify([...reported, transactionId].slice(-20)))
    } catch {
      // Private mode: Ads still deduplicates by transaction_id.
    }
    return false
  }

  function gtag(): Gtag {
    const w = window as unknown as { dataLayer?: unknown[], gtag?: Gtag }
    w.dataLayer = w.dataLayer || []
    // Same as the head snippet in nuxt.config.ts: it must push `arguments`.
    w.gtag = w.gtag || function () { w.dataLayer!.push(arguments) } // eslint-disable-line prefer-rest-params
    return w.gtag
  }

  /** `transactionId` when one is known (the guest token), so Ads can drop duplicates. */
  function trackPurchase(planId?: PremiumPlanId, transactionId?: string) {
    if (!import.meta.client) return
    if (transactionId && alreadyReported(transactionId)) return

    const plan = planId ? getPlan(planId) : undefined
    const value = plan?.price
    const currency = plan?.currency ?? 'USD'

    try {
      gtag()('event', 'conversion', {
        send_to: ADS_PURCHASE,
        value,
        currency,
        transaction_id: transactionId ?? '',
      })
      gtag()('event', 'purchase', {
        send_to: GA4_ID,
        value,
        currency,
        transaction_id: transactionId,
        items: planId ? [{ item_id: planId, item_name: planId, price: value, quantity: 1 }] : undefined,
      })
    } catch (err) {
      console.error('Could not report the purchase to Google:', err)
    }
  }

  /** Called once a checkout URL exists, whichever button opened it. */
  function trackBeginCheckout(planId?: string, variantId?: string) {
    if (!import.meta.client) return

    const plan = plans.value.find(p => p.id === planId) ?? plans.value.find(p => p.variantId === variantId)

    try {
      gtag()('event', 'conversion', {
        send_to: ADS_BEGIN_CHECKOUT,
        value: BEGIN_CHECKOUT_VALUE,
        currency: 'USD',
      })
      gtag()('event', 'begin_checkout', {
        send_to: GA4_ID,
        value: plan?.price,
        currency: plan?.currency ?? 'USD',
        items: plan ? [{ item_id: plan.id, item_name: plan.id, price: plan.price, quantity: 1 }] : undefined,
      })
    } catch (err) {
      console.error('Could not report the checkout to Google:', err)
    }
  }

  return { trackPurchase, trackBeginCheckout }
}
